import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FiArrowLeft, FiUser, FiBook, FiClipboard, FiCheckCircle, FiFileText, FiSettings, FiPlus, FiTrash2, FiCheck, FiX, FiEdit, FiMic, FiEye, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { adminAPI, studentModulesAPI, studentFormulasAPI, homeworkAPI } from '../../../services/api';
import { prepareHomeworkHtml } from '../../../utils/homeworkContent';
import './StudentProfile.css';

const STUDENT_PROFILE_TAB_IDS = ['info', 'modules', 'formulas', 'homework', 'checking', 'tests'];

const StudentProfile = () => {
    const { studentId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const isAdminUser = user?.role === 'admin';
    const [student, setStudent] = useState(null);
    const [loading, setLoading] = useState(true);
    const requestedTab = searchParams.get('tab');
    const activeTab = STUDENT_PROFILE_TAB_IDS.includes(requestedTab) ? requestedTab : 'info';

    useEffect(() => {
        loadStudent();
    }, [studentId]);

    const loadStudent = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getStudent(studentId);
            console.log('Full response:', response);
            console.log('Response data:', response.data);
            // Данные находятся в response.data.student, а не в response.data напрямую
            const studentData = response.data.student || response.data;
            console.log('Student data:', studentData);
            console.log('Username:', studentData.username);
            console.log('Email:', studentData.email);
            setStudent(studentData);
        } catch (error) {
            console.error('Ошибка загрузки ученика:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="student-profile-loading">Загрузка...</div>;
    }

    if (!student) {
        return <div className="student-profile-error">Ученик не найден</div>;
    }

    console.log('Rendering with student:', student);

    const tabs = [
        { id: 'info', label: 'Информация', icon: <FiUser /> },
        { id: 'modules', label: 'Модули обучения', icon: <FiBook /> },
        { id: 'formulas', label: 'Речевые формулы', icon: <FiCheckCircle /> },
        { id: 'homework', label: 'Домашние задания', icon: <FiFileText /> },
        { id: 'checking', label: 'Проверка заданий', icon: <FiClipboard /> },
        { id: 'tests', label: 'Результаты тестов', icon: <FiSettings /> },
    ];

    const handleTabClick = (tabId) => {
        if (tabId === 'info') {
            setSearchParams({});
        } else {
            setSearchParams({ tab: tabId });
        }
    };

    return (
        <div className="student-profile">
            {/* Заголовок */}
            <div className="student-profile-header">
                <button 
                    onClick={() => navigate('/admin/students')}
                    className="btn-back-profile"
                >
                    <FiArrowLeft />
                    Назад к списку
                </button>
                
                <div className="student-profile-info">
                    <div className="student-avatar">
                        <FiUser size={40} />
                    </div>
                    <div>
                        <h1>{student.username || student.first_name || 'Студент'}</h1>
                        <p className="student-email">{student.email || 'Email не указан'}</p>
                    </div>
                </div>
            </div>

            {/* Табы навигации */}
            <div className="student-profile-tabs">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => handleTabClick(tab.id)}
                    >
                        {tab.icon}
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* Контент */}
            <div className="student-profile-content">
                {activeTab === 'info' && (
                    <StudentInfoTab student={student} onUpdate={loadStudent} canEdit={isAdminUser} />
                )}
                {activeTab === 'modules' && (
                    <StudentModulesTab studentId={studentId} isAdmin={isAdminUser} />
                )}
                {activeTab === 'formulas' && <StudentFormulasTab studentId={studentId} />}
                {activeTab === 'homework' && <StudentHomeworkTab studentId={studentId} student={student} />}
                {activeTab === 'checking' && <StudentCheckingTab studentId={studentId} />}
                {activeTab === 'tests' && <StudentTestsTab studentId={studentId} />}
            </div>
        </div>
    );
};

// Вкладка "Информация"
const StudentInfoTab = ({ student, onUpdate, canEdit }) => {
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({
        username: student?.username || '',
        email: student?.email || '',
    });

    const handleSave = async () => {
        try {
            await adminAPI.updateStudent(student.id, formData);
            setEditing(false);
            onUpdate();
        } catch (error) {
            console.error('Ошибка обновления:', error);
            alert('Ошибка при сохранении');
        }
    };

    return (
        <div className="profile-tab-content">
            <div className="info-section">
                <h2>Основная информация</h2>
                
                {editing ? (
                    <div className="info-form">
                        <div className="form-group">
                            <label>Имя пользователя</label>
                            <input
                                type="text"
                                value={formData.username}
                                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Email</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>
                        <div className="form-actions">
                            <button onClick={() => setEditing(false)} className="btn-cancel">Отмена</button>
                            <button onClick={handleSave} className="btn-save">Сохранить</button>
                        </div>
                    </div>
                ) : (
                    <div className="info-display">
                        <div className="info-item">
                            <label>Имя пользователя:</label>
                            <span>{student.username}</span>
                        </div>
                        <div className="info-item">
                            <label>Email:</label>
                            <span>{student.email}</span>
                        </div>
                        <div className="info-item">
                            <label>Дата регистрации:</label>
                            <span>
                                {student.created_at 
                                    ? new Date(student.created_at).toLocaleDateString('ru-RU')
                                    : 'Н/Д'
                                }
                            </span>
                        </div>
                        {canEdit && (
                            <button type="button" onClick={() => setEditing(true)} className="btn-edit">
                                Редактировать
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const ModuleLessonAccessEditor = ({ studentId, moduleId }) => {
    const [open, setOpen] = useState(false);
    const [lessons, setLessons] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [allLevelLessons, setAllLevelLessons] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadLessons = async () => {
        setLoading(true);
        try {
            const { data } = await studentModulesAPI.getModuleLessons(studentId, moduleId);
            setLessons(data.lessons || []);
            setSelected(new Set(data.assigned_lesson_ids || []));
            setAllLevelLessons(Boolean(data.all_level_lessons));
        } catch (error) {
            console.error('Ошибка загрузки уроков модуля:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            loadLessons();
        }
    }, [open, studentId, moduleId]);

    const toggleLesson = (lessonId) => {
        const next = new Set(selected);
        if (next.has(lessonId)) {
            next.delete(lessonId);
        } else {
            next.add(lessonId);
        }
        setSelected(next);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await studentModulesAPI.setModuleLessons(studentId, moduleId, {
                lesson_ids: [...selected],
            });
            await loadLessons();
        } catch (error) {
            console.error('Ошибка сохранения уроков:', error);
            alert(error.response?.data?.error || 'Не удалось сохранить');
        } finally {
            setSaving(false);
        }
    };

    const handleOpenAllLevel = async () => {
        if (!window.confirm('Открыть ученику все уроки этого уровня?')) return;
        setSaving(true);
        try {
            await studentModulesAPI.setModuleLessons(studentId, moduleId, { all_level_lessons: true });
            await loadLessons();
        } catch (error) {
            console.error('Ошибка:', error);
            alert(error.response?.data?.error || 'Не удалось сохранить');
        } finally {
            setSaving(false);
        }
    };

    const startPickingLessons = () => {
        setAllLevelLessons(false);
        setSelected(new Set());
    };

    return (
        <div className="module-lessons-editor">
            <button
                type="button"
                className="btn-module-lessons-toggle"
                onClick={() => setOpen(!open)}
            >
                {open ? <FiChevronUp /> : <FiChevronDown />}
                Доступные уроки
            </button>
            {open && (
                <div className="module-lessons-panel">
                    {loading ? (
                        <p>Загрузка уроков…</p>
                    ) : allLevelLessons ? (
                        <>
                            <p className="module-lessons-hint">
                                Сейчас ученику доступны <strong>все</strong> уроки этого уровня.
                            </p>
                            <div className="module-lessons-actions">
                                <button
                                    type="button"
                                    className="btn-save"
                                    disabled={saving}
                                    onClick={startPickingLessons}
                                >
                                    Выбрать конкретные уроки
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="module-lessons-hint">
                                Ученик видит только отмеченные уроки. Пустой список после сохранения — доступа к
                                урокам этого модуля не будет.
                            </p>
                            <ul className="module-lessons-checklist">
                                {lessons.map((lesson) => (
                                    <li key={lesson.id}>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={selected.has(lesson.id)}
                                                onChange={() => toggleLesson(lesson.id)}
                                            />
                                            <span>{lesson.title}</span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                            {lessons.length === 0 && <p className="empty-hint">В уровне нет активных уроков</p>}
                            <div className="module-lessons-actions">
                                <button
                                    type="button"
                                    className="btn-save"
                                    disabled={saving}
                                    onClick={handleSave}
                                >
                                    Сохранить список
                                </button>
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    disabled={saving}
                                    onClick={handleOpenAllLevel}
                                >
                                    Все уроки уровня
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const StudentModulesTab = ({ studentId, isAdmin }) => {
    const [modules, setModules] = useState([]);
    const [availableAreas, setAvailableAreas] = useState([]);
    const [availableLevels, setAvailableLevels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedArea, setSelectedArea] = useState('');
    const [selectedLevel, setSelectedLevel] = useState('');

    useEffect(() => {
        loadData();
    }, [studentId, isAdmin]);

    const loadData = async () => {
        try {
            setLoading(true);
            if (isAdmin) {
                const [modulesRes, availableRes] = await Promise.all([
                    studentModulesAPI.getStudentModules(studentId),
                    studentModulesAPI.getAvailableModules(),
                ]);
                setModules(modulesRes.data.modules || []);
                setAvailableAreas(availableRes.data.areas || []);
                setAvailableLevels(availableRes.data.levels || []);
            } else {
                const modulesRes = await studentModulesAPI.getStudentModules(studentId);
                setModules(modulesRes.data.modules || []);
                setAvailableAreas([]);
                setAvailableLevels([]);
            }
        } catch (error) {
            console.error('Ошибка загрузки модулей:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddModule = async () => {
        if (!selectedArea || !selectedLevel) {
            alert('Выберите область и уровень');
            return;
        }

        try {
            await studentModulesAPI.assignModule(studentId, {
                area_id: selectedArea,
                level_id: selectedLevel,
            });
            setShowAddModal(false);
            setSelectedArea('');
            setSelectedLevel('');
            loadData();
        } catch (error) {
            console.error('Ошибка назначения модуля:', error);
            alert(error.response?.data?.error || 'Ошибка при назначении модуля');
        }
    };

    const handleRemoveModule = async (moduleId) => {
        if (!window.confirm('Удалить этот модуль?')) return;

        try {
            await studentModulesAPI.removeModule(studentId, moduleId);
            loadData();
        } catch (error) {
            console.error('Ошибка удаления модуля:', error);
            alert('Ошибка при удалении модуля');
        }
    };

    if (loading) {
        return <div className="profile-tab-content">Загрузка...</div>;
    }

    return (
        <div className="profile-tab-content">
            <div className="modules-header">
                <h2>Назначенные модули обучения</h2>
                {isAdmin && (
                    <button onClick={() => setShowAddModal(true)} className="btn-add-module">
                        <FiPlus />
                        Назначить модуль
                    </button>
                )}
            </div>

            {!isAdmin && (
                <p className="module-lessons-hint" style={{ marginBottom: '1rem' }}>
                    Назначение модулей доступно только администратору. Вы можете ограничивать список уроков
                    в каждом уже назначенном модуле.
                </p>
            )}

            {modules.length === 0 ? (
                <div className="empty-state">
                    <p>У студента пока нет назначенных модулей</p>
                    <p className="empty-hint">
                        {isAdmin
                            ? 'Назначьте модули, чтобы открыть доступ к урокам'
                            : 'Администратор должен назначить модуль; затем здесь можно настроить уроки'}
                    </p>
                </div>
            ) : (
                <div className="modules-list">
                    {modules.map((module) => (
                        <div key={module.id} className="module-card">
                            <div className="module-info">
                                <div className="module-header-info">
                                    <h3>{module.area_name}</h3>
                                    <span className={`category-badge ${module.category}`}>
                                        {module.category === 'grammar' ? 'Грамматика' : 'Лексика'}
                                    </span>
                                </div>
                                <p className="module-level">Уровень: {module.level_name}</p>
                                <div className="module-meta">
                                    <span>Назначено: {new Date(module.assigned_at).toLocaleDateString('ru-RU')}</span>
                                    {module.assigned_by_name && (
                                        <span>Кем: {module.assigned_by_name}</span>
                                    )}
                                </div>
                                <ModuleLessonAccessEditor studentId={studentId} moduleId={module.id} />
                            </div>
                            {isAdmin && (
                                <button
                                    onClick={() => handleRemoveModule(module.id)}
                                    className="btn-remove-module"
                                    title="Удалить модуль"
                                    type="button"
                                >
                                    <FiTrash2 />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {isAdmin && showAddModal && (
                <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Назначить модуль</h3>

                        <div className="form-group">
                            <label>Область обучения</label>
                            <select
                                value={selectedArea}
                                onChange={(e) => setSelectedArea(e.target.value)}
                            >
                                <option value="">Выберите область</option>
                                {availableAreas.map((area) => (
                                    <option key={area.id} value={area.id}>
                                        {area.name} ({area.category === 'grammar' ? 'Грамматика' : 'Лексика'})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Уровень</label>
                            <select
                                value={selectedLevel}
                                onChange={(e) => setSelectedLevel(e.target.value)}
                            >
                                <option value="">Выберите уровень</option>
                                {availableLevels.map((level) => (
                                    <option key={level.id} value={level.id}>
                                        {level.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="modal-actions">
                            <button onClick={() => setShowAddModal(false)} className="btn-cancel">
                                Отмена
                            </button>
                            <button onClick={handleAddModule} className="btn-save">
                                Назначить
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const StudentFormulasTab = ({ studentId }) => {
    const [formulas, setFormulas] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        loadData();
    }, [studentId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [formulasRes, statsRes] = await Promise.all([
                studentFormulasAPI.getStudentFormulas(studentId),
                studentFormulasAPI.getFormulaStats(studentId)
            ]);
            setFormulas(formulasRes.data.formulas || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Ошибка загрузки формул:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (formulaId, currentStatus) => {
        try {
            await studentFormulasAPI.updateFormulaStatus(studentId, formulaId, {
                is_mastered: !currentStatus
            });
            loadData();
        } catch (error) {
            console.error('Ошибка обновления статуса:', error);
            alert('Ошибка при обновлении статуса');
        }
    };

    const filteredFormulas = formulas.filter(formula => {
        const matchesFilter = 
            filter === 'all' ||
            (filter === 'mastered' && formula.is_mastered === 1) ||
            (filter === 'not_mastered' && formula.is_mastered === 0) ||
            (filter === 'not_started' && formula.is_mastered === null);
        
        const matchesSearch = 
            formula.formula_ru.toLowerCase().includes(searchQuery.toLowerCase()) ||
            formula.formula_en.toLowerCase().includes(searchQuery.toLowerCase());

        return matchesFilter && matchesSearch;
    });

    if (loading) {
        return <div className="profile-tab-content">Загрузка...</div>;
    }

    return (
        <div className="profile-tab-content">
            <div className="formulas-header">
                <h2>Речевые формулы</h2>
                {stats && (
                    <div className="formulas-stats">
                        <div className="stat-item stat-total">
                            <span className="stat-label">Всего:</span>
                            <span className="stat-value">{stats.total_formulas}</span>
                        </div>
                        <div className="stat-item stat-mastered">
                            <span className="stat-label">Усвоено:</span>
                            <span className="stat-value">{stats.mastered_count || 0}</span>
                        </div>
                        <div className="stat-item stat-not-mastered">
                            <span className="stat-label">Не усвоено:</span>
                            <span className="stat-value">{stats.not_mastered_count || 0}</span>
                        </div>
                        <div className="stat-item stat-not-started">
                            <span className="stat-label">Не начато:</span>
                            <span className="stat-value">{stats.not_started_count || 0}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Фильтры и поиск */}
            <div className="formulas-controls">
                <div className="formulas-filters">
                    <button 
                        className={`filter-btn-formula ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        Все
                    </button>
                    <button 
                        className={`filter-btn-formula ${filter === 'mastered' ? 'active' : ''}`}
                        onClick={() => setFilter('mastered')}
                    >
                        Усвоено
                    </button>
                    <button 
                        className={`filter-btn-formula ${filter === 'not_mastered' ? 'active' : ''}`}
                        onClick={() => setFilter('not_mastered')}
                    >
                        Не усвоено
                    </button>
                    <button 
                        className={`filter-btn-formula ${filter === 'not_started' ? 'active' : ''}`}
                        onClick={() => setFilter('not_started')}
                    >
                        Не начато
                    </button>
                </div>
                <input
                    type="text"
                    placeholder="Поиск формул..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="formula-search"
                />
            </div>

            {/* Список формул */}
            <div className="formulas-list">
                {filteredFormulas.length === 0 ? (
                    <div className="empty-state">
                        <p>Формулы не найдены</p>
                    </div>
                ) : (
                    filteredFormulas.map(formula => (
                        <div key={formula.id} className="formula-card">
                            <div className="formula-content">
                                <div className="formula-texts">
                                    <div className="formula-ru">{formula.formula_ru}</div>
                                    <div className="formula-en">{formula.formula_en}</div>
                                </div>
                                {formula.context && (
                                    <div className="formula-examples">
                                        <div className="example-ru">Контекст: {formula.context}</div>
                                    </div>
                                )}
                            </div>
                            <div className="formula-actions">
                                <button
                                    className={`btn-formula-status ${formula.is_mastered === 1 ? 'mastered' : formula.is_mastered === 0 ? 'not-mastered' : 'not-started'}`}
                                    onClick={() => handleToggleStatus(formula.id, formula.is_mastered === 1)}
                                >
                                    {formula.is_mastered === 1 ? (
                                        <>
                                            <FiCheck />
                                            Усвоено
                                        </>
                                    ) : (
                                        <>
                                            <FiX />
                                            {formula.is_mastered === 0 ? 'Не усвоено' : 'Отметить'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

const StudentHomeworkTab = ({ studentId, student }) => {
    const [homeworks, setHomeworks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [formData, setFormData] = useState({
        description: ''
    });
    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ color: [] }, { background: [] }],
            ['link'],
            ['clean']
        ]
    };

    useEffect(() => {
        loadHomeworks();
    }, [studentId]);

    const loadHomeworks = async () => {
        try {
            setLoading(true);
            // Получаем ДЗ для конкретного студента через параметр запроса
            console.log('Loading homework for studentId:', studentId);
            const response = await homeworkAPI.getAllHomework({ studentId });
            const studentHomeworks = response.data.homeworks || [];
            
            console.log('API response:', response.data);
            console.log('Loaded homeworks for student:', studentHomeworks);
            setHomeworks(studentHomeworks);
        } catch (error) {
            console.error('Ошибка загрузки ДЗ:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateHomework = async () => {
        const description = formData.description || '';
        if (!description.trim() || description === '<p><br></p>') {
            alert('Заполните описание задания');
            return;
        }

        try {
            await homeworkAPI.createHomework({
                ...formData,
                studentIds: [studentId]
            });
            setShowCreateModal(false);
            setFormData({ description: '' });
            loadHomeworks();
        } catch (error) {
            console.error('Ошибка создания ДЗ:', error);
            alert(error.response?.data?.error || 'Ошибка при создании ДЗ');
        }
    };

    const handleDeleteHomework = async (hwId) => {
        if (!window.confirm('Удалить это домашнее задание?')) return;

        try {
            await homeworkAPI.deleteHomework(hwId);
            loadHomeworks();
        } catch (error) {
            console.error('Ошибка удаления ДЗ:', error);
            alert('Ошибка при удалении ДЗ');
        }
    };

    if (loading) {
        return <div className="profile-tab-content">Загрузка...</div>;
    }

    return (
        <div className="profile-tab-content">
            <div className="homework-header">
                <h2>Домашние задания для {student.username}</h2>
                <button onClick={() => setShowCreateModal(true)} className="btn-add-homework">
                    <FiPlus />
                    Отправить ДЗ
                </button>
            </div>

            {homeworks.length === 0 ? (
                <div className="empty-state">
                    <p>У студента пока нет домашних заданий</p>
                </div>
            ) : (
                <div className="homework-list">
                    {homeworks.map(hw => {
                        const studentInfo = hw.students?.find(s => s.student_id === parseInt(studentId));
                        return (
                            <div key={hw.id} className="homework-card">
                                <div className="homework-info">
                                    <h3>{hw.title}</h3>
                                    <div
                                        className="homework-description"
                                        dangerouslySetInnerHTML={{
                                            __html: prepareHomeworkHtml(hw.description, 'admin')
                                        }}
                                    />
                                    <div className="homework-meta">
                                        <span>Отправлено: {new Date(hw.created_at).toLocaleDateString('ru-RU')}</span>
                                        {studentInfo && (
                                            <span className={studentInfo.is_read ? 'status-read' : 'status-unread'}>
                                                {studentInfo.is_read ? '✓ Прочитано' : '○ Не прочитано'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <button 
                                    onClick={() => handleDeleteHomework(hw.id)}
                                    className="btn-delete-homework"
                                    title="Удалить"
                                >
                                    <FiTrash2 />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content modal-homework" onClick={(e) => e.stopPropagation()}>
                        <h3>Отправить домашнее задание</h3>
                        
                        <div className="form-group">
                            <label>Описание *</label>
                            <div className="quill-wrapper profile-homework-quill">
                                <ReactQuill
                                    value={formData.description}
                                    onChange={(value) => setFormData({ ...formData, description: value })}
                                    modules={quillModules}
                                    placeholder="Введите описание ДЗ (можно вставлять ссылки)"
                                    theme="snow"
                                />
                            </div>
                            <small>Совет: Вы можете вставить ссылку на конкретный блок урока</small>
                        </div>

                        <div className="modal-actions">
                            <button onClick={() => setShowCreateModal(false)} className="btn-cancel">
                                Отмена
                            </button>
                            <button onClick={handleCreateHomework} className="btn-save">
                                Отправить
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const StudentCheckingTab = ({ studentId }) => {
    const [submissions, setSubmissions] = useState([]);
    const [audioSubmissions, setAudioSubmissions] = useState([]);
    const [homeworkCount, setHomeworkCount] = useState(0);
    const [audioCount, setAudioCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [activeType, setActiveType] = useState('homework');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        loadSubmissions();
    }, [studentId, activeType, statusFilter]);

    const loadSubmissions = async () => {
        try {
            setLoading(true);

            const baseParams = { studentId };
            const params = statusFilter !== 'all'
                ? { ...baseParams, status: statusFilter }
                : baseParams;

            // Всегда подгружаем оба источника: активный список + корректные счетчики табов
            const [homeworkRes, audioRes] = await Promise.all([
                adminAPI.getHomeworkCheckingSubmissions(params),
                adminAPI.getAudioSubmissions(params)
            ]);

            const homeworkList = homeworkRes.data.submissions || [];
            const audioList = audioRes.data.submissions || [];
            setSubmissions(homeworkList);
            setAudioSubmissions(audioList);
            setHomeworkCount(homeworkList.length);
            setAudioCount(audioList.length);
        } catch (error) {
            console.error('Ошибка загрузки заданий:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            pending: { label: 'Черновик', class: 'status-pending' },
            submitted: { label: 'На проверке', class: 'status-pending' },
            reviewed: { label: 'Проверено', class: 'status-checked' },
            checked: { label: 'Проверено', class: 'status-checked' }
        };
        return statusMap[status] || { label: status, class: '' };
    };

    if (loading) {
        return <div className="profile-tab-content">Загрузка...</div>;
    }

    const currentSubmissions = activeType === 'homework' ? submissions : audioSubmissions;

    return (
        <div className="profile-tab-content">
            <div className="checking-header">
                <h2>Проверка заданий</h2>
            </div>

            {/* Переключатель типа заданий */}
            <div className="checking-type-tabs">
                <button
                    className={`type-tab ${activeType === 'homework' ? 'active' : ''}`}
                    onClick={() => setActiveType('homework')}
                >
                    <FiEdit />
                    ДЗ ({homeworkCount})
                </button>
                <button
                    className={`type-tab ${activeType === 'audio' ? 'active' : ''}`}
                    onClick={() => setActiveType('audio')}
                >
                    <FiMic />
                    Аудио ({audioCount})
                </button>
            </div>

            {/* Фильтр по статусу */}
            <div className="checking-filters">
                <button 
                    className={`filter-btn-check ${statusFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setStatusFilter('all')}
                >
                    Все
                </button>
                <button 
                    className={`filter-btn-check ${statusFilter === 'submitted' ? 'active' : ''}`}
                    onClick={() => setStatusFilter('submitted')}
                >
                    На проверке
                </button>
                <button 
                    className={`filter-btn-check ${statusFilter === 'reviewed' ? 'active' : ''}`}
                    onClick={() => setStatusFilter('reviewed')}
                >
                    Проверено
                </button>
            </div>

            {/* Список заданий */}
            {currentSubmissions.length === 0 ? (
                <div className="empty-state">
                    <p>Заданий для проверки нет</p>
                </div>
            ) : (
                <div className="submissions-list">
                    {currentSubmissions.map(sub => {
                        const statusInfo = getStatusBadge(sub.status);
                        return (
                            <div key={sub.id} className="submission-card">
                                <div className="submission-info">
                                    <div className="submission-header-info">
                                        <h3>{sub.homework_title || 'ДЗ'}</h3>
                                        <span className={`status-badge ${statusInfo.class}`}>
                                            {statusInfo.label}
                                        </span>
                                    </div>
                                    <div className="submission-meta">
                                        {sub.submitted_at ? (
                                            <span>Отправлено: {new Date(sub.submitted_at).toLocaleString('ru-RU')}</span>
                                        ) : (
                                            <span>Черновик</span>
                                        )}
                                        {sub.reviewed_at && (
                                            <span>Проверено: {new Date(sub.reviewed_at).toLocaleString('ru-RU')}</span>
                                        )}
                                    </div>
                                    {sub.teacher_comment && (
                                        <div className="submission-feedback">
                                            Комментарий: {sub.teacher_comment}
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => window.open(
                                        activeType === 'audio'
                                            ? `/admin/checking/audio?studentId=${studentId}`
                                            : `/admin/checking/homework/${sub.id}`,
                                        '_blank'
                                    )}
                                    className="btn-view-submission"
                                    title={activeType === 'audio' ? 'Открыть проверку аудио' : 'Открыть ДЗ'}
                                >
                                    <FiEye />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const StudentTestsTab = ({ studentId }) => {
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadTests();
    }, [studentId]);

    const loadTests = async () => {
        try {
            setLoading(true);
            console.log('Loading tests for studentId:', studentId);
            const response = await adminAPI.getStandaloneTestResults({ studentId });
            console.log('Test results response:', response.data);
            
            // Фильтруем только тесты с блоками (новая система)
            const allTests = response.data.results || [];
            const testsWithBlocks = allTests.filter(test => test.has_blocks);
            
            setTests(testsWithBlocks);
        } catch (error) {
            console.error('Ошибка загрузки тестов:', error);
        } finally {
            setLoading(false);
        }
    };

    const getScoreColor = (percentage) => {
        if (percentage >= 80) return '#52c41a';
        if (percentage >= 60) return '#faad14';
        return '#f5222d';
    };

    if (loading) {
        return <div className="profile-tab-content">Загрузка...</div>;
    }

    return (
        <div className="profile-tab-content">
            <div className="tests-header">
                <h2>Результаты тестов</h2>
                <div className="tests-stats">
                    <span>Всего попыток: {tests.length}</span>
                    {tests.length > 0 && (
                        <>
                            <span>Средний балл: {(tests.reduce((sum, t) => {
                                const percentage = Number(t.writing_checked && t.final_percentage !== null 
                                    ? t.final_percentage 
                                    : t.percentage);
                                return sum + percentage;
                            }, 0) / tests.length).toFixed(1)}%</span>
                            <span>Пройдено: {tests.filter(t => {
                                const percentage = Number(t.writing_checked && t.final_percentage !== null 
                                    ? t.final_percentage 
                                    : t.percentage);
                                return percentage >= Number(t.passing_score || 70);
                            }).length}</span>
                        </>
                    )}
                </div>
            </div>

            {tests.length === 0 ? (
                <div className="empty-state">
                    <p>Студент еще не проходил тесты</p>
                </div>
            ) : (
                <div className="tests-list">
                    {tests.map(test => {
                        const displayPercentage = Number(test.writing_checked && test.final_percentage !== null 
                            ? test.final_percentage 
                            : test.percentage);
                        const displayScore = Number(test.writing_checked && test.final_score !== null 
                            ? test.final_score 
                            : test.score);
                        const maxScore = Number(test.max_score) || 100;
                        const isPassed = displayPercentage >= (Number(test.passing_score) || 70);
                        const isChecking = test.has_writing && !test.writing_checked;
                        
                        return (
                            <div key={test.id} className="test-card">
                                <div className="test-info">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                        <h3>{test.test_title || 'Тест без названия'}</h3>
                                        {isChecking && (
                                            <span style={{
                                                padding: '4px 12px',
                                                background: '#faad14',
                                                color: 'white',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: '600'
                                            }}>
                                                На проверке
                                            </span>
                                        )}
                                    </div>
                                    <div className="test-details">
                                        <span>Дата прохождения: {new Date(test.completed_at).toLocaleString('ru-RU')}</span>
                                        <span>Время: {test.time_spent ? `${Math.floor(test.time_spent / 60)} мин` : 'Н/Д'}</span>
                                        {test.has_writing && (
                                            <span style={{ color: '#722ed1' }}>Письменная часть</span>
                                        )}
                                    </div>
                                    {!isChecking && (
                                        <>
                                            <div className="test-results">
                                                <div className="test-score" style={{ color: getScoreColor(displayPercentage) }}>
                                                    <strong>{displayScore}/{maxScore} ({displayPercentage.toFixed(1)}%)</strong>
                                                </div>
                                                <span className={`test-status ${isPassed ? 'passed' : 'failed'}`}>
                                                    {isPassed ? '✓ Пройден' : '✗ Не пройден'}
                                                </span>
                                            </div>
                                            {test.writing_checked && (
                                            <div style={{ 
                                                marginTop: '10px', 
                                                padding: '8px 12px', 
                                                background: '#f0f0f0', 
                                                borderRadius: '6px',
                                                fontSize: '13px'
                                            }}>
                                                <strong>Автоматическая оценка:</strong> {Number(test.score)}/{Number(test.max_score)} ({Number(test.percentage).toFixed(1)}%)
                                                <br />
                                                <strong>Итоговая оценка:</strong> {Number(test.final_score)}/{Number(test.max_score)} ({Number(test.final_percentage).toFixed(1)}%)
                                            </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default StudentProfile;
